import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import DeleteIcon from "@mui/icons-material/Delete";
import BookmarkIcon from "@mui/icons-material/Bookmark";

import { DELETE_USER_BOOK_BOOKMARK } from "arena/queries";

/**
 * Диалог со списком всех закладок книги
 */
export default function BookmarkListDialog({
  open,
  onClose,
  bookmarks,
  onBookmarkClick,
  onBookmarkDeleted,
}) {
  const { t } = useTranslation("arena");
  const { enqueueSnackbar } = useSnackbar();
  const [deletingBookmarks, setDeletingBookmarks] = useState(new Set());

  const [deleteBookmark] = useMutation(DELETE_USER_BOOK_BOOKMARK, {
    onCompleted: (data) => {
      const deletedId = data.deleteUserBookBookmark.id;
      setDeletingBookmarks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(deletedId);
        return newSet;
      });
      enqueueSnackbar(
        t("bookReader.bookmarkList.deleteSuccess", "Закладка удалена"),
        { variant: "success" }
      );
      onBookmarkDeleted?.(data.deleteUserBookBookmark);
    },
    onError: (error) => {
      console.error("Ошибка удаления закладки:", error);
      enqueueSnackbar(
        t("bookReader.bookmarkList.deleteError", "Ошибка удаления закладки"),
        { variant: "error" }
      );
      setDeletingBookmarks(new Set()); // Сбрасываем все состояния загрузки
    },
  });

  const handleBookmarkClick = (bookmark) => {
    onBookmarkClick?.(bookmark);
    onClose();
  };

  const handleDeleteBookmark = async (bookmarkId, e) => {
    e.stopPropagation(); // Предотвращаем срабатывание клика на ListItem

    setDeletingBookmarks((prev) => new Set(prev).add(bookmarkId));

    try {
      await deleteBookmark({
        variables: { id: bookmarkId },
      });
    } catch (error) {
      console.error("Ошибка при удалении закладки:", error);
      setDeletingBookmarks((prev) => {
        const newSet = new Set(prev);
        newSet.delete(bookmarkId);
        return newSet;
      });
    }
  };

  // Сортируем закладки по прогрессу (позиции в книге)
  const sortedBookmarks = [...(bookmarks || [])].sort(
    (a, b) => a.character - b.character
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <BookmarkIcon />
        {t("bookReader.bookmarkList.title", "Мои закладки")}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {!bookmarks || bookmarks.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body1" color="text.secondary">
              {t(
                "bookReader.bookmarkList.empty",
                "У вас пока нет закладок в этой книге"
              )}
            </Typography>
          </Box>
        ) : (
          <List>
            {sortedBookmarks.map((bookmark, index) => (
              <React.Fragment key={bookmark.id}>
                <ListItem
                  button
                  onClick={() => handleBookmarkClick(bookmark)}
                  disabled={deletingBookmarks.has(bookmark.id)}
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      bookmark.note ||
                      t(
                        "bookReader.bookmarkList.untitled",
                        "Закладка без названия"
                      )
                    }
                    secondary={
                      <React.Fragment>
                        {bookmark.chapterName && (
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {bookmark.chapterName}
                            <br />
                          </Typography>
                        )}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          {t(
                            "bookReader.bookmarkList.progressRead",
                            "{{percent}}% прочитано",
                            { percent: bookmark.progressPercent?.toFixed(1) }
                          )}
                          {bookmark.createdAt && (
                            <>
                              {" "}
                              •{" "}
                              {new Date(
                                bookmark.createdAt
                              ).toLocaleDateString()}
                            </>
                          )}
                        </Typography>
                      </React.Fragment>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label={t(
                        "bookReader.bookmarkList.delete",
                        "Удалить закладку"
                      )}
                      onClick={(e) => handleDeleteBookmark(bookmark.id, e)}
                      disabled={deletingBookmarks.has(bookmark.id)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < sortedBookmarks.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("common.close", "Закрыть")}</Button>
      </DialogActions>
    </Dialog>
  );
}

BookmarkListDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bookmarks: PropTypes.array,
  onBookmarkClick: PropTypes.func,
  onBookmarkDeleted: PropTypes.func,
};
